import { ReactNode, useEffect } from "react"
import { Navigate, useLocation } from "react-router-dom"

import { BackButtonWithHistory, HeaderLogo, HeaderRoot } from "components/Header"
import { Spinner } from "components/Spinner"
import { useGetBookingHash } from "modules/Call/routing"
import { resolveRedirect } from "modules/Call/PsyRedirects"
import { CallBookingPsychologist, useGetPsyCall } from "modules/Call/queries"
import { CALL_PAGE, OWNER } from "modules/Call/common/constants"
import { LobbyPageLayout } from "modules/Call/components/LobbyPageLayout"
import { LobbyVideo } from "modules/Call/components/LobbyVideo"
import { LobbyInfoForPsy } from "modules/Call/components/LobbyInfoForPsy"
import { useInputDevicesContext } from "modules/Call/InputDevices"
import { isServerOrConnectionError } from "utils/helpers/isServerOrConnectionError"

export function PsyLobbyPage({
    voximplantUserName,
    renderAvatar,
}: {
    voximplantUserName: string
    renderAvatar: (psy: CallBookingPsychologist, isHidden: boolean) => ReactNode
}) {
    const bookingHash = useGetBookingHash(true)
    const location = useLocation()
    const [{ cameraEnabled }] = useInputDevicesContext()

    const { isFetching, data, isError, error, refetch } = useGetPsyCall(
        bookingHash,
        CALL_PAGE.LOBBY,
        true
    )

    useEffect(() => {
        void refetch()
    }, [location])

    if (isError && !isServerOrConnectionError(error))
        return <Navigate to="/psychologist-account" replace />
    if ((isFetching && !data) || !data) return <Spinner />

    const redirect = resolveRedirect(data.status, CALL_PAGE.LOBBY)
    if (redirect) return redirect({ bookingHash: bookingHash })

    return (
        <LobbyPageLayout
            header={
                <HeaderRoot isJustifyContent="start">
                    <BackButtonWithHistory />
                    <HeaderLogo>alter</HeaderLogo>
                </HeaderRoot>
            }
            left={
                <LobbyVideo
                    avatar={renderAvatar(data.booking.psychologist, cameraEnabled)}
                    voximplantUserName={voximplantUserName}
                    generateTokenApiUrl="/call/v2/psychologist/generate-token"
                    owner={OWNER.Psychologist}
                    bookingHash={bookingHash}
                />
            }
            right={
                <LobbyInfoForPsy
                    clientName={data.booking.client.name}
                    sessionDatetime={data.booking.session_start_datetime}
                    sessionDuration={data.booking.session_duration}
                    clientId={data.booking.client.id}
                    hasQuestionnaireAnswers={data.booking.client.has_questionnaire_answers}
                    bookingHash={bookingHash}
                    callStatus={data.status}
                    companionLocation={data.companion_status}
                />
            }
        />
    )
}
